import { Field } from '@/components/ui';
import type { RegisterEvent, RegisterKind } from '@/features/registers/api';
import styles from './OperationalRecords.module.css';
import { FieldInput } from './register-fields';
import { formatValue, toFormValue, type StaffNames } from './register-format';
import type { CorrectForm } from './register-correct';

/** RL-2018: the drawer's "Correct" form: the register's fields and a reason. */
export function CorrectSection({
  kind: k,
  form,
  idPrefix,
  staffOptions,
}: {
  kind: RegisterKind;
  form: CorrectForm;
  idPrefix: string;
  staffOptions: { value: string; label: string }[];
}) {
  const general = form.error && !form.error.field ? form.error.message : null;
  return (
    <section className={styles.section} aria-label="Correct this record">
      <h3 className={styles.sectionTitle}>Correct this record</h3>
      <p className={styles.note}>
        Change only what was recorded wrong. The status does not change here.
        The values before and after and your reason are kept on the timeline.
      </p>
      {general && (
        <p className={styles.late} role="alert">
          {general}
        </p>
      )}
      {k.fields.map((f) => (
        <FieldInput
          key={f.key}
          field={f}
          idPrefix={idPrefix}
          value={form.values[f.key] ?? toFormValue(f, null)}
          staffOptions={staffOptions}
          error={form.error?.field === f.key ? form.error.message : undefined}
          onChange={(v) => form.setValue(f.key, v)}
        />
      ))}
      <Field
        label="Reason for the correction"
        hint="3 to 500 characters. Kept on the timeline with the change."
      >
        <textarea
          className={styles.textarea}
          value={form.reason}
          maxLength={500}
          onChange={(e) => form.setReason(e.target.value)}
        />
      </Field>
    </section>
  );
}

const asMap = (v: unknown) =>
  v && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;

/** RL-2018: "How serious: Medium → High" lines of a "Corrected" step. */
export function CorrectionChanges({
  e,
  kind: k,
  names,
}: {
  e: RegisterEvent;
  kind: RegisterKind;
  names: StaffNames;
}) {
  const before = asMap(e.data?.before);
  const after = asMap(e.data?.after);
  if (e.action !== 'updated' || !before || !after) return null;
  return (
    <>
      {Object.keys(after).map((key) => {
        const f = k.fields.find((x) => x.key === key) ?? {
          key,
          label: key,
          type: 'text' as const,
        };
        return (
          <p key={key} className={styles.text}>
            {f.label}: {formatValue(f, before[key], names)} →{' '}
            {formatValue(f, after[key], names)}
          </p>
        );
      })}
    </>
  );
}
